import { useImperativeHandle, useRef, type Ref } from "react";
import {
  TextInput,
  type NativeSyntheticEvent,
  type StyleProp,
  type TextInputChangeEventData,
  type TextStyle,
} from "react-native";

import type {
  EnrichedMarkdownTextInputInstance,
  EnrichedMarkdownTextInputProps,
} from "react-native-enriched-markdown/lib/typescript/src/EnrichedMarkdownTextInput";

function noopMeasure() {
  /* no-op on web */
}

export function EnrichedMarkdownTextInput(
  props: EnrichedMarkdownTextInputProps & { ref?: Ref<EnrichedMarkdownTextInputInstance | null> },
) {
  const { ref } = props;
  const {
    autoCapitalize,
    autoFocus,
    cursorColor,
    defaultValue,
    editable,
    multiline = true,
    onBlur,
    onChangeMarkdown,
    onChangeText,
    onFocus,
    placeholder,
    placeholderTextColor,
    selectionColor,
    style,
  } = props;

  const inputRef = useRef<TextInput | null>(null);
  const valueRef = useRef<string>(defaultValue ?? "");

  useImperativeHandle(
    ref,
    (): EnrichedMarkdownTextInputInstance => ({
      blur: () => {
        inputRef.current?.blur();
      },
      copyToClipboard: () => {
        if (typeof navigator !== "undefined" && navigator.clipboard) {
          void navigator.clipboard.writeText(valueRef.current);
        }
      },
      focus: () => {
        inputRef.current?.focus();
      },
      getCaretRect: () => Promise.resolve({ height: 0, width: 0, x: 0, y: 0 }),
      getMarkdown: () => Promise.resolve(valueRef.current),
      indentList: () => {},
      insertLink: () => {},
      insertMention: () => {},
      insertText: () => {},
      measure: noopMeasure as EnrichedMarkdownTextInputInstance["measure"],
      measureInWindow: noopMeasure as EnrichedMarkdownTextInputInstance["measureInWindow"],
      measureLayout: noopMeasure as EnrichedMarkdownTextInputInstance["measureLayout"],
      outdentList: () => {},
      removeLink: () => {},
      setLink: () => {},
      setSelection: () => {},
      setValue: (markdown: string) => {
        valueRef.current = markdown;
        // TextInput on web reads `value` from props for controlled inputs only.
        // Use the imperative DOM-style setter to update the uncontrolled input.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- web TextInput ref lacks setNativeProps typing
        (inputRef.current as any)?.setNativeProps?.({ text: markdown });
      },
      startMention: () => {},
      toggleBold: () => {},
      toggleHeading: () => {},
      toggleItalic: () => {},
      toggleOrderedList: () => {},
      toggleSpoiler: () => {},
      toggleStrikethrough: () => {},
      toggleUnderline: () => {},
      toggleUnorderedList: () => {},
    }),
    [],
  );

  return (
    <TextInput
      autoCapitalize={autoCapitalize as never}
      autoFocus={autoFocus}
      cursorColor={cursorColor}
      defaultValue={defaultValue}
      editable={editable}
      multiline={multiline}
      onBlur={onBlur}
      onChange={(event: NativeSyntheticEvent<TextInputChangeEventData>) => {
        const text = event.nativeEvent.text;
        valueRef.current = text;
        onChangeText?.(text);
        onChangeMarkdown?.(text);
      }}
      onFocus={onFocus}
      placeholder={placeholder}
      placeholderTextColor={placeholderTextColor}
      ref={inputRef}
      selectionColor={selectionColor}
      style={style as StyleProp<TextStyle>}
    />
  );
}
